import { formatCurrency } from '@angular/common';
import { profileFeatures } from '@advisor-ui/app-services';
import { entityType, orderStatus, orderStatusIndex } from '@trade-platform/lib-enums';
import {
    BaseOrder,
    DocumentFile,
    OrderCurrentApproval,
    Profile,
    ProfileRepCode,
    Role
} from '@trade-platform/ui-shared';
import { ApiFormErrorItem, objectHasValue } from '@trade-platform/ui-utils';

type Steps = typeof orderStatusIndex;

export const userHasOrderRepcode = (profile: Profile, orderRepCode: ProfileRepCode) => {
    return profile.repCodes?.some(repCode => repCode.id === orderRepCode.id);
};

export const getOrderStatusIndex = (status: string): number => {
    const index = (orderStatusIndex as Record<string, number>)[status];
    return index ?? -1;
};

export const isAdminAndCanEditOrder = (profile: Profile, orderFirmId: string) => {
    const isAdmin = profile.roles.some(role => role.name === 'admin');
    const isInOrderFirm = profile.firm && profile.firm.id === orderFirmId;
    const isInOrderFirmParent =
        profile.organization &&
        profile.organization.subTenants &&
        !!profile.organization.subTenants.find(
            subTenant =>
                subTenant.type === entityType.firm && subTenant.entityId.toString() === orderFirmId
        );
    return isAdmin && (isInOrderFirm || isInOrderFirmParent);
};

/**
 * Sponsor-side means the profile belongs to a fund sponsor and to no firm. This mirrors the
 * API's own test (`user.scopes.organization.type === fundSponsor`), which is what decides
 * whether the sponsor cancel rules apply to a request, so the two stay in step.
 */
export const isFundSponsorSide = (profile: Profile) =>
    !!profile.fundSponsor?.id && !profile.firm?.id;

export const isFundSponsorAdmin = (profile: Profile) => {
    const isAdmin = profile.roles.some(role => role.name === 'admin');

    return isAdmin && isFundSponsorSide(profile);
};

/**
 * A fund-sponsor admin may cancel an order that is still a draft, when the `cancelDraftOrders`
 * feature is enabled for them. Firm-side users are excluded because they already have cancel
 * through `canCancelOrder()`, and this must not widen that.
 *
 * sysAdmin is excluded deliberately, even though it is the role a sponsor's own administrator
 * usually holds: it owns the admin dashboard rather than orders, it has no Orders nav entry, and
 * the Roles & Permissions page shown to customers scores System Admin zero on Cancel Orders.
 *
 * The API enforces the same rule and additionally requires five business days without
 * activity — a window the client cannot evaluate, since `updatedAt` is not on the order
 * payload. A refused cancel therefore surfaces as the API's 400 through the existing
 * failure notification rather than by hiding the button.
 *
 * refs trade-platform/trade-platform#10426
 */
export const canFundSponsorCancelOrder = (profile: Profile, status: string) => {
    const isSponsorAdmin = profile.roles.some(role => role.name === 'admin');
    const isFundSponsor = isFundSponsorSide(profile);
    const hasCancelDraftOrders = !!profile.features?.some(
        feature => feature.name === profileFeatures.cancelDraftOrders
    );

    return isSponsorAdmin && isFundSponsor && hasCancelDraftOrders && status === orderStatus.draft;
};

/**
 * The server's own explanation of a refused cancel, when it is safe to show verbatim — otherwise
 * null, and the caller falls back to the generic support copy.
 *
 * 401 and 403 are excluded even though they are 4xx and carry a message: UnauthorizedInterceptor
 * calls callLogout() on both before any component can render them, so classifying them as
 * displayable would promise something the app cannot deliver. That is also why the sponsor gate
 * reports every refusal a person can reach as 400 — wrong role and feature-not-enabled as well
 * as the two state refusals. Only its M2M refusal is still a 403, and no browser sees that one.
 * See assertSponsorMayCancel in lib-orders.
 *
 * refs trade-platform/trade-platform#10426
 */
export const cancelRefusalText = (error: {
    status: number;
    error?: { message?: unknown } | null;
}): string | null => {
    const isDisplayableRefusal =
        error.status >= 400 && error.status < 500 && error.status !== 401 && error.status !== 403;
    const message = error.error?.message;

    return isDisplayableRefusal && typeof message === 'string' && message.length > 0
        ? message
        : null;
};

// The roles that have always been able to cancel an order from the detail page. Deliberately
// narrower than the cancel route's allowedRoles, which also admits admin and reviewer.
const ORDER_ACTOR_CANCEL_ROLES = [
    'authorizer',
    'signer',
    'submitter',
    'advisor',
    'activeInvestor',
    'assistant'
];

/**
 * Whether the Cancel Order button renders on the order-detail page.
 *
 * Two populations, deliberately kept apart: firm-side order actors keep the cancel they
 * already had, at any status short of cancelled/completed/archived; a fund-sponsor admin gets
 * the narrow draft-only grant instead. Shared by the purchase and maintenance detail pages so
 * the two cannot drift.
 *
 * refs trade-platform/trade-platform#10426
 */
export const canCancelOrderForProfile = (
    profile: Profile | null | undefined,
    roles: string[],
    status: string | undefined | null
) => {
    if (!status) {
        return false;
    }

    if (profile && canFundSponsorCancelOrder(profile, status)) {
        return true;
    }

    const isOrderActor = roles.some(role => ORDER_ACTOR_CANCEL_ROLES.includes(role));

    return (
        isOrderActor &&
        status !== orderStatus.cancelled &&
        status !== orderStatus.completed &&
        status !== orderStatus.archived
    );
};

/**
 * Whether the Cancel Order button on the process pages (overview, form sidebar) is greyed out.
 *
 * Two populations, as on the detail page. Firm-side users keep the historical rule exactly:
 * greyed when they have read-only access, live otherwise, at every status. Sponsor-side admins
 * are gated positively on the grant itself -- feature plus draft -- rather than inferred from
 * read-only.
 *
 * The inference is what failed in UAT. hasReadOnlyAccess is false whenever
 * isAdminAndCanEditOrder is true, and that covers an `admin` whose ORGANISATION lists the
 * order's firm as a sub-tenant, not just someone inside the order's firm. So a fund-sponsor
 * admin of a tenant with firm sub-tenants is not read-only, and the previous
 * `isReadOnly && !canFundSponsorCancelOrder(...)` never consulted the grant for them: the
 * button was live before the feature was granted, and live on non-draft orders after.
 *
 * refs trade-platform/trade-platform#10426
 */
export const isProcessCancelDisabled = (
    profile: Profile | null | undefined,
    isReadOnly: boolean,
    status: string | undefined | null
) => {
    // Sponsor-SIDE, not sponsor-admin: a sponsor sysAdmin is deliberately outside the grant, so
    // the button must be greyed for them rather than left live to fail on click. Keying off the
    // admin role would drop them back onto the read-only rule, which is false for a sponsor
    // whose organisation owns the order's firm.
    if (profile && isFundSponsorSide(profile)) {
        return !canFundSponsorCancelOrder(profile, status ?? '');
    }

    return isReadOnly;
};

export const isHoldingOptionAdmin = (profile: Profile) => {
    const isAdmin = profile.roles.some(role => role.name === 'admin');
    const isHoldingOption = !!profile.holdingOption?.id && !profile.firm?.id;

    return isAdmin && isHoldingOption;
};

export const isReviewer = (profile: Profile) => {
    return profile.roles.some(role => role.name === 'reviewer');
};

export const hasReadOnlyAccess = (profile: Profile, orderFirmId: string) => {
    const isAdminAndCanEditOrderValue = isAdminAndCanEditOrder(profile, orderFirmId);
    const isFundSponsorAdminValue = isFundSponsorAdmin(profile);
    const isHoldingOptionAdminValue = isHoldingOptionAdmin(profile);
    const isReviewerValue = isReviewer(profile);

    // is advisor/investor/assistant
    const isOrderEditor = profile.roles.some(
        role =>
            role.name === 'advisor' || role.name === 'assistant' || role.name === 'activeInvestor'
    );

    return (
        !isAdminAndCanEditOrderValue &&
        !isOrderEditor &&
        (isFundSponsorAdminValue || isHoldingOptionAdminValue || isReviewerValue)
    );
};

// Order view by role and status:
// - sysAdmin, reviewer: always sees order details unless overridden by another role
// - admin, advisor, assistant, activeInvestor: sees order process if order is in steps 1 - 5, detail otherwise
//   - exception: if order is in presignature review and user can review, user lands on order details
export const userSeesOrderProcess = (
    profile: Profile,
    status: string,
    orderRepCode: ProfileRepCode,
    orderFirmId: string
) => {
    const hasOrderEditorRole =
        profile.roles.filter(
            role =>
                role.name === 'advisor' ||
                role.name === 'assistant' ||
                role.name === 'activeInvestor'
        ).length > 0;

    if (
        hasOrderEditorRole &&
        userHasOrderRepcode(profile, orderRepCode) &&
        orderStatusIndex[status as keyof Steps] <
            getOrderStatusIndex(orderStatus.pendingFirmApproval)
    ) {
        return true;
    }

    if (
        (isAdminAndCanEditOrder(profile, orderFirmId) || hasReadOnlyAccess(profile, orderFirmId)) &&
        orderStatusIndex[status as keyof Steps] <
            getOrderStatusIndex(orderStatus.pendingFirmApproval)
    ) {
        if (
            profile.roles.some(role => role.name === 'reviewer') &&
            status === orderStatus.pendingPresignatureReview
        ) {
            return false;
        }
        return true;
    }

    return false;
};

export const trainingMessages = {
    trainingCompleted: () =>
        `<span class="u-success-green"><span class="fa-regular fa-circle-check u-mr8"></span>The soliciting advisor has completed training.</span>`,
    trainingCompletedMultiple: () =>
        `<span class="u-success-green"><span class="fa-regular fa-circle-check u-mr8"></span>All advisors have completed training.</span>`,
    trainingRequiredAdvisors: (requiredTrainingUsers: string[]) =>
        `<span class="u-error-red"><span class="fa-regular fa-circle-xmark u-mr8"></span>Training Required: ${requiredTrainingUsers.join(
            ', '
        )}. All advisors must complete this requirement to continue.</span>`,
    trainingRequiredPartialAdvisors: (
        completedTrainingUsers: string[],
        requiredTrainingUsers: string[]
    ) => `
    <p class="u-success-green u-mb8">
        <span class="fa-regular fa-circle-check u-mr8"></span>Training Complete: ${completedTrainingUsers.join(
            ', '
        )}.
    </p>
    <p class="u-error-red">
        <span class="fa-regular fa-circle-check u-mr8"></span>Training Required: ${requiredTrainingUsers.join(
            ', '
        )}. All advisors must complete this requirement in order to continue.
    </p>`,
    trainingRequiredSolicitingAdvisor: (requiredTrainingUsers: string[]) =>
        `<span class="u-error-red"><span class="fa-regular fa-circle-xmark u-mr8"></span>Training Required: ${requiredTrainingUsers.join(
            ', '
        )}. Only the soliciting advisor needs to complete training.</span>`
};

export const hasEntityPermission = (
    currentApproval: OrderCurrentApproval | undefined,
    profile: Profile
) => {
    if (currentApproval && currentApproval.actingEntityType && currentApproval.actingEntityId) {
        switch (currentApproval.actingEntityType) {
            case 'firm':
                return (
                    !!profile.firm && profile.firm.id === currentApproval.actingEntityId.toString()
                );
            case 'holdingOption':
                return (
                    !!profile.holdingOption &&
                    profile.holdingOption.id === currentApproval.actingEntityId.toString()
                );
            case 'fundSponsor':
                return (
                    !!profile.fundSponsor &&
                    profile.fundSponsor.id === currentApproval.actingEntityId
                );
        }
    }
    return false;
};

export const hasApproverRole = (currentApprovalRoles: string[], userRoles: Role[]): boolean => {
    const userRoleStrings = userRoles.map(role => role.name);
    return userRoleStrings.filter(role => currentApprovalRoles.includes(role)).length > 0;
};

export const hasEntityApprovalPermission = (
    currentApproval: OrderCurrentApproval | undefined,
    profile: Profile
): boolean =>
    objectHasValue(currentApproval) &&
    hasEntityPermission(currentApproval, profile) &&
    hasApproverRole(currentApproval.roles, profile.roles);

/**
 * Formats the given number amount to a currency string (en-US, $, USD, 0.2-2);
 * @param amount The number amount to format;
 */
export const formatAmountToCurrency = (amount: number) => {
    return formatCurrency(amount, 'en-US', '$', 'USD', '0.2-2');
};

/**
 * Pulls the number amount data out of the given error message;
 * @param message The error message to pull the amount data out of;
 */
const getMinimumAmountFromErrorMessage = (message: string) => {
    if (message) {
        const matcher = message.match(/(\d*\,?\.?)(\d*)/g);
        return matcher !== null ? parseFloat(matcher.join('').split(',').join('')) : null;
    }
    return null;
};

export const minimumInvestmentNotifications = {
    error: (error: ApiFormErrorItem, purchaseType: string) => {
        const message = [];
        const minimumAmount = formatAmountToCurrency(
            getMinimumAmountFromErrorMessage(error.message) as number
        );
        message.push('You may not proceed with purchases below the minimum purchase amount.');
        if (minimumAmount !== null) {
            message.push(`${purchaseType} purchase minimum is ${minimumAmount}.`);
        }
        return message.join('<br>');
    }
};

export const isDisabledFile = (file: File | DocumentFile) => {
    if (!file || !file.name || file.name.lastIndexOf('.') < 0) {
        return false;
    }
    const fileType = file.name.substring(file.name.lastIndexOf('.') + 1);
    return fileType === 'csv' || fileType === 'xlsx';
};

/**
 * A file's `File.id` is regenerated whenever the file is replaced — DocuSign write-back,
 * finalize, NIGO and the replace endpoint all soft delete the old row and insert a new one.
 * `formId` survives, so both identities have to resolve: `formId` for links built after this
 * change, `File.id` for URLs captured before it.
 *
 * refs trade-platform/trade-platform#7593
 */
export const getDocumentIndexById = (filesToDisplay: DocumentFile[], id: string | null) => {
    /*
      Rejects any falsy id, not only null: `currentDocument` starts out as '' and files that are not
      forms carry formId '', so without this an absent id resolves to the first non-form file
      instead of "no match" — activating the wrong document on load.
    */
    if (!id) {
        return -1;
    }
    return filesToDisplay.findIndex(item => item.id === id || item.formId === id);
};

/** The id to put in a document-viewer route for a known file: its stable `formId` when it has one. */
export const getFileRouteId = (file: DocumentFile) => file.formId || file.id;

/**
 * The id to put in a document-viewer route, resolved from a possibly-stale id. Prefers the stable
 * `formId` so a link built from an order loaded before a replacement still resolves afterwards. An
 * id matching no current file is passed through unchanged, leaving the viewer to fall back and tell
 * the user why. Passing no id yields the first document.
 */
export const getDocumentRouteId = (
    filesToDisplay: DocumentFile[],
    docId: string | null = null
): string | null => {
    // Falsy covers both the no-argument call and the no-document route, which navigates with ''.
    const index = !docId ? 0 : getDocumentIndexById(filesToDisplay, docId);
    const file = index >= 0 ? filesToDisplay[index] : undefined;

    if (!file) {
        return docId;
    }
    return getFileRouteId(file);
};

export const isAboveTotalFileSizeLimit = (order: BaseOrder) => {
    const totalFileSizeLimit = 35000000;
    const totalFileSize = order.files
        .filter(file => file.isUserUploaded && file.type === 'form')
        .reduce((acc, cur) => acc + (cur.sizeInBytes ?? 0), 0);

    return totalFileSize > totalFileSizeLimit;
};
